import { createDebug } from '@/utils/debug'
import { documentScopedKey } from '@/services/office/ppt-online-binding'

// AHA-43274: "does ANY add-in frame in this document own the slide currently on screen?"
//
// A frame that resolves MINE for the shown slide publishes a claim naming that slide. Every other
// frame can then read it, which is what lets the frame that was presenting tell a HANDOVER (the
// show moved onto another AhaSlides slide — keep the quiz running) from a plain PowerPoint slide
// (nobody owns it — end the quiz).
//
// WHY A LIVE CLAIM AND NOT A STORED SET. The obvious alternative is a registry of the Common-API
// ids of every slide carrying an AhaSlides add-in, and a "shown id is not in the set" test. It
// cannot work: localStorage is machine-local, so a deck authored on one machine and presented on
// another starts with an EMPTY registry and every slide reads as a miss — the quiz would stop the
// moment the show started. slide-binding-history's own comment records the same weakness for the
// same reason. A claim carries no such assumption, because it is published by the frame that is
// live right now rather than recovered from a previous session.
//
// It also sidesteps a second, unproven assumption. Keying a registry off the Rich API slide id
// (toCommonSlideId) is only known to agree with getSelectedDataAsync on the WEB — insert-slides'
// id probe measured 257 from both APIs there, and says nothing about desktop. A claim is written
// and read using the SAME getSelectedDataAsync id the poll already holds, so the two ends agree by
// construction on both platforms.
//
// Split from ppt-online-current-slide, which is a different question on the same transport: that
// module tells a STARVED frame which slide is up (a handover of a document-global fact, web only).
// This one tells every frame whether the shown slide is OWNED, and runs on both platforms — hence
// no isPptOnlineAddin() gate here. Desktop frames publish and read it too.
//
// Toggle logging with: localStorage.debug = 'aha:ppt:auto-start' — the same namespace as the
// ownership check and the auto-start flow, so a stop decision and the claim it turned on read as
// one trace.
const log = createDebug('ppt:auto-start')

const SHOW_OWNER_KEY_PREFIX = 'aha:ppt:show-owner-claim:'

const getShowOwnerKey = () => documentScopedKey(SHOW_OWNER_KEY_PREFIX)

// WHO wrote the claim, which is what the veto actually turns on — see loadShowOwnerClaim's
// `byThisFrame` and armStopIfStillUnowned. Generated once per frame load, and it only has to be
// unique among the add-in frames of one document alive at one time.
//
// Deliberately not derived from anything meaningful. The frame HAS no stable identity to derive it
// from: Office exposes no per-instance id, and a copy/pasted add-in duplicates every document-scoped
// property it does expose. A random token per load is the one thing that cannot collide by being
// copied.
export const SHOW_OWNER_FRAME_TOKEN = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

// A claim outlives the Slide Show that wrote it, so one left by a PREVIOUS run must not vouch for
// this one. Hygiene only, and NOT what makes that safe — see the proof in armStopIfStillUnowned:
// whether a claim is this frame's own is decided by its token, not its age, and the frame that arms
// a stop is by construction the last frame to have published. This bound just keeps a claim from
// being read at all once it is obviously past use, so a frame that has since been deleted from its
// slide cannot veto forever. Same value and same job as ppt-online-current-slide's
// CURRENT_SLIDE_MAX_AGE_MS, so deliberately the same number.
//
// Nothing REMOVES the key. It is one document-scoped entry shared by every add-in frame, and
// clearing on the way out would delete the claim of whichever frame is still presenting. See
// stopTagSlideFocusPolling for the specific interleaving that made this a live-quiz bug. The key is
// overwritten by the next owner and pruned on read; that is enough.
export const CLAIM_MAX_AGE_MS = 60000

// Publishes "this frame owns the slide PowerPoint is showing", naming the frame and the slide.
//
// The TOKEN is what the veto turns on. `at` is only the age bound's input, and the slide id rides
// along for the trace — every decision logged in this flow names the slide it was about, and a veto
// is not worth being the exception.
export const publishShowOwnerClaim = (commonSlideId) => {
  const key = getShowOwnerKey()
  if (!key || commonSlideId === null || commonSlideId === undefined || commonSlideId === '') return
  try {
    localStorage.setItem(key, JSON.stringify({
      commonSlideId,
      at: Date.now(),
      frameToken: SHOW_OWNER_FRAME_TOKEN,
    }))
    log('show owner claim published | shown ppt slide id =', commonSlideId,
      '| frame =', SHOW_OWNER_FRAME_TOKEN)
  } catch (error) {
    log('publish show owner claim failed', error)
  }
}

// Resolves { commonSlideId, at, byThisFrame } or null. Null on every failure — no key (the host
// exposes no document.url), nothing stored, unparsable, or older than CLAIM_MAX_AGE_MS — because
// every caller treats "no claim" as "nobody has said they own the shown slide", and a claim we
// cannot read is exactly that.
//
// `byThisFrame` is the whole answer the veto needs: false means SOME OTHER add-in frame was the last
// to declare itself owner of the show. A claim with no token at all reads as another frame's, which
// is the safe direction — the only writer that stamps no token is a build older than this one, and
// treating it as foreign leaves a quiz running rather than ending someone else's.
export const loadShowOwnerClaim = () => {
  const key = getShowOwnerKey()
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.commonSlideId === null || parsed.commonSlideId === undefined) return null
    const age = Date.now() - (parsed.at || 0)
    if (age > CLAIM_MAX_AGE_MS) {
      log('show owner claim ignored | stale by', age, 'ms |', parsed.commonSlideId)
      return null
    }
    return {
      commonSlideId: parsed.commonSlideId,
      at: parsed.at || 0,
      byThisFrame: parsed.frameToken === SHOW_OWNER_FRAME_TOKEN,
    }
  } catch (error) {
    log('read show owner claim failed', error)
    return null
  }
}
